/**
 * Sanitizes a file name by removing special characters that can cause issues in URLs.
 * This is especially useful when uploading files to services like Google Cloud Storage
 * where special characters in filenames can cause broken URLs.
 *
 * @param {File} file - The file object to sanitize
 * @returns {File} - A new File object with the sanitized name or the original file if no sanitization was needed
 */
export const sanitizeFileName = (file) => {
  if (!file) return file;

  // Remove special characters that can cause issues in URLs like #, ?, &, etc.
  // Replace spaces with hyphens for better URL compatibility
  const sanitizedName = file.name.replace(/[#?&]/g, "").replace(/\s/g, "-");

  // If the name hasn't changed, return the original file
  if (sanitizedName === file.name) {
    return file;
  }

  // Create a new file with the sanitized name
  return new File([file], sanitizedName, { type: file.type });
};

/**
 * Sanitize multiple files by applying sanitizeFileName to each file in an array
 *
 * @param {File[]} files - Array of file objects to sanitize
 * @returns {File[]} - Array of sanitized file objects
 */
export const sanitizeFiles = (files) => {
  if (!files || !Array.isArray(files)) return files;
  return files.map(sanitizeFileName);
};
